import { ErrorBoundary } from '@sentry/react';
import { SideNavigation, SideNavigationHeader } from '@statsbomb/kitbag-components';
import { useAtom, useAtomValue } from 'jotai';
import { ReactNode } from 'react';
import { navigationOpenAtom } from '@/atoms/navigation';
import { unwrappedUserNavOverridesAtom } from '@/atoms/queries/user';
import { SupportLinks } from '@/components/topBar/SupportLinks';
import { useTeamNavigationClassic, useTeamNavigationNew } from '@/hooks/navigation/useTeamNavigation';
import { useIsTouchDevice } from '@/hooks/useIsTouchDevice';
import LogoIcon from '../../../images/logos/hudlLogo.svg';
import LogoText from '../../../images/logos/hudlStatsbombTextLogo.svg';

const LogoLinkWithData = ({ id, children }: { id: string; children: ReactNode }) => {
  const userNavOverrides = useAtomValue(unwrappedUserNavOverridesAtom);
  const newXpDefaultPath = `${useTeamNavigationNew()}dashboard`;
  const oldXpDefaultPath = `${useTeamNavigationClassic()}dashboard`;
  const pathLink = userNavOverrides.team ? oldXpDefaultPath : newXpDefaultPath;

  return (
    <a href={pathLink} data-testid={`logo-link-${id}`}>
      <div>{children}</div>
    </a>
  );
};

/* This is handling an error with the `useSinglePlatformNavigationItems` hook which tries to call
the api for the default team and player ids */
const LogoLink = ({ id, children }: { id: string; children: JSX.Element }) => (
  <ErrorBoundary fallback={children}>
    <LogoLinkWithData id={id}>{children}</LogoLinkWithData>
  </ErrorBoundary>
);

export const SideNav = ({
  SideNavMainComponent,
  SideNavFooterComponent,
}: {
  SideNavMainComponent: React.FC;
  SideNavFooterComponent: React.FC;
}) => {
  const [isNavOpen, setIsNavOpen] = useAtom(navigationOpenAtom);
  const isTouch = useIsTouchDevice();
  const isNavOpenState = isTouch ? isNavOpen : false;

  return (
    <SideNavigation isOpen={isNavOpenState} isTouch={isTouch}>
      <SideNavigationHeader
        logoIcon={
          <LogoLink id="icon">
            <LogoIcon />
          </LogoLink>
        }
        logoText={
          <LogoLink id="text">
            <LogoText />
          </LogoLink>
        }
        onClick={isTouch ? () => setIsNavOpen(!isNavOpen) : undefined}
        optionalControl={
          <div className="relative">
            <SupportLinks isMobile />
          </div>
        }
      />
      <SideNavMainComponent />
      <SideNavFooterComponent />
    </SideNavigation>
  );
};
